/**
 * Event Schema Definitions for Analytics
 * 
 * These schemas define the expected structure of event_data payloads
 * for each event type. The schema version is tracked to handle migrations.
 */

export type SchemaFieldType = 
  | 'string'
  | 'string?'
  | 'number'
  | 'number?'
  | 'boolean'
  | 'boolean?'
  | 'object'
  | 'object?';

export interface EventSchema {
  version: number;
  schema: Record<string, SchemaFieldType>;
}

export const EventSchemas: Record<string, EventSchema> = {
  page_view: {
    version: 1,
    schema: {
      page_path: 'string',
      page_title: 'string?',
      referrer: 'string?',
      time_on_page_seconds: 'number?',
    },
  },

  video_engagement: {
    version: 1,
    schema: {
      video_id: 'string',
      video_title: 'string?',
      action: 'string', // 'play' | 'pause' | 'seek' | 'complete' | 'progress'
      current_time_seconds: 'number?',
      duration_seconds: 'number?',
      completion_percentage: 'number?',
      playback_speed: 'number?',
    },
  },

  article_engagement: {
    version: 1,
    schema: {
      article_id: 'string',
      article_title: 'string?',
      action: 'string', // 'view' | 'scroll' | 'complete' | 'bookmark'
      scroll_percentage: 'number?',
      time_on_page_seconds: 'number?',
    },
  },

  audio_engagement: {
    version: 1,
    schema: {
      audio_id: 'string',
      audio_title: 'string?',
      action: 'string', // 'play' | 'pause' | 'seek' | 'complete' | 'progress'
      current_time_seconds: 'number?',
      duration_seconds: 'number?',
      completion_percentage: 'number?',
    },
  },

  button_click: {
    version: 1,
    schema: {
      button_name: 'string',
      button_location: 'string?',
      context: 'object?',
    },
  },

  feature_usage: {
    version: 1,
    schema: {
      feature_name: 'string',
      action: 'string?',
      context: 'object?',
    },
  },

  user_journey: {
    version: 1,
    schema: {
      step_name: 'string',
      step_number: 'number?',
      journey_name: 'string?',
      context: 'object?',
    },
  },
};

/**
 * Validates event data against its schema
 * Returns true if valid, false with console warning if invalid
 */
export function validateEventData(
  eventType: string,
  eventData: Record<string, unknown>
): { valid: boolean; errors: string[] } {
  const schema = EventSchemas[eventType];
  const errors: string[] = [];

  if (!schema) {
    // Unknown event type - allow but log warning
    console.warn(`[Analytics] No schema defined for event type: ${eventType}`);
    return { valid: true, errors: [] };
  }

  // Validate required fields
  for (const [field, type] of Object.entries(schema.schema)) {
    const isOptional = type.endsWith('?');
    const baseType = type.replace('?', '') as 'string' | 'number' | 'boolean' | 'object';
    const fieldValue = eventData[field];

    // Check required fields
    if (!isOptional && (fieldValue === undefined || fieldValue === null)) {
      errors.push(`Missing required field: ${field}`);
      continue;
    }

    // Skip type check for optional fields that are not present
    if (fieldValue === undefined || fieldValue === null) {
      continue;
    }

    // Type checking
    const actualType = typeof fieldValue;
    if (baseType === 'object') {
      if (actualType !== 'object' || Array.isArray(fieldValue)) {
        errors.push(`Field ${field} expected object, got ${actualType}`);
      }
    } else if (actualType !== baseType) {
      errors.push(`Field ${field} expected ${baseType}, got ${actualType}`);
    }
  }

  if (errors.length > 0) {
    console.warn(`[Analytics] Event validation errors for ${eventType}:`, errors);
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Get the current schema version for an event type
 */
export function getSchemaVersion(eventType: string): number {
  return EventSchemas[eventType]?.version ?? 1;
}

/**
 * Sanitize event data to only include expected fields
 */
export function sanitizeEventData(
  eventType: string,
  eventData: Record<string, unknown>
): Record<string, unknown> {
  const schema = EventSchemas[eventType];
  
  if (!schema) {
    return eventData; // No schema, pass through
  }

  const sanitized: Record<string, unknown> = {};
  const expectedFields = Object.keys(schema.schema);

  for (const field of expectedFields) {
    if (field in eventData) {
      sanitized[field] = eventData[field];
    }
  }

  // Also preserve any extra fields that start with underscore (metadata)
  for (const [key, value] of Object.entries(eventData)) {
    if (key.startsWith('_') || key === 'context') {
      sanitized[key] = value;
    }
  }

  return sanitized;
}
